import { faArrowDown, faArrowsV, faCameraMovie, faBurn, faTimes, faCameraRetro, faRetweetAlt } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@material-ui/core";
import ImagePlayer, { HORIZONTAL_NAV_HEIGHT, VERTICAL_NAV_WIDTH } from "components/display/image/ImagePlayer";
import { playlistDistanceToTimestamp } from "components/util/GeometryUtilsV2";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";
import { togglePictureInPicture } from "redux/actions";
import ShoeCamIcon from "./ShoeCamIcon";

const THERMAL_FFV = "Thermal FFV (Thermal FFV)";
const THERMAL_FFV_LEGACY = "Thermal FFV (Thermal)";
const VISIBLE_FFV = "Visible FFV (Visible FFV)";
const VISIBLE_FFV_LEGACY = "Visible FFV";
const VISIBLE_FFV_DOME = "Visible FFV (Dome)";
const VISIBLE_FFV_DOME_2 = "Visible FFV (Dome FFV)";
const VISIBLE_PANTOGRAPH = "Visible Pantograph";
const VISIBLE_OVERHEAD = "Visible Overhead (Pan)";
const VISIBLE_LINESIDE_RIGHT = "Visible Lineside (right)";
const VISIBLE_LINESIDE_LEFT = "Visible Lineside (left)";

const NINE_SIXTEEN = 0.5625;

const DEFAULT_WIDTH_HEIGHT = { width: 300, height: 300 * NINE_SIXTEEN };

const FORMATTED_STREAMS = {
    [THERMAL_FFV_LEGACY]: <FontAwesomeIcon icon={faBurn} />,
    [THERMAL_FFV]: <FontAwesomeIcon icon={faBurn} />,
    [VISIBLE_FFV]: <FontAwesomeIcon icon={faCameraMovie} />,
    [VISIBLE_OVERHEAD]: <FontAwesomeIcon icon={faArrowsV} />,
    [VISIBLE_PANTOGRAPH]: <FontAwesomeIcon icon={faArrowsV} />,
    // prettier-ignore
    [VISIBLE_LINESIDE_LEFT]: <FontAwesomeIcon icon={faArrowDown} style={{ transform: "rotate(45deg)" }} />,
    // prettier-ignore
    [VISIBLE_LINESIDE_RIGHT]: <FontAwesomeIcon icon={faArrowDown} style={{ transform: "rotate(-45deg)" }} />,
    [VISIBLE_FFV_DOME]: <FontAwesomeIcon icon={faCameraMovie} />,
    [VISIBLE_FFV_DOME_2]: <FontAwesomeIcon icon={faCameraMovie} />,
    [VISIBLE_FFV_LEGACY]: <FontAwesomeIcon icon={faCameraRetro} />,
};

/**
 *
 * @param {string} stream
 * @returns {ReactNode}
 */
const getIcon = (stream) => {
    if (stream in FORMATTED_STREAMS) {
        return FORMATTED_STREAMS[stream];
    }

    const lowerCaseStream = stream.toLowerCase();

    if (lowerCaseStream.includes("shoe cam 1")) {
        return <ShoeCamIcon shoeCamNumber={1} />;
    }
    if (lowerCaseStream.includes("shoe cam 2")) {
        return <ShoeCamIcon shoeCamNumber={2} />;
    }
    if (lowerCaseStream.includes("visible lineside") && lowerCaseStream.includes("right")) {
        // prettier-ignore
        return <FontAwesomeIcon icon={faArrowDown} style={{ transform: "rotate(-45deg)" }} />;
    }
    if (lowerCaseStream.includes("visible lineside") && lowerCaseStream.includes("left")) {
        // prettier-ignore
        return <FontAwesomeIcon icon={faArrowDown} style={{ transform: "rotate(45deg)" }} />;
    }
    if (lowerCaseStream.includes("visible")) {
        return <FontAwesomeIcon icon={faCameraMovie} />;
    }
    if (lowerCaseStream.includes("thermal")) {
        return <FontAwesomeIcon icon={faBurn} />;
    }
    return stream;
};

/**
 * @param {pipWidth} number
 * @param {pipHeight} number
 * @param {containerWidth} number
 * @param {containerHeight} number
 * @returns {{x: number, y: number}}
 */
const calculateXY = (pipWidth, pipHeight, containerWidth, containerHeight) => {
    const x = containerWidth - pipWidth - VERTICAL_NAV_WIDTH;
    const y = containerHeight - pipHeight - HORIZONTAL_NAV_HEIGHT;

    return { x, y };
};

const clampToBounds = (x, y, pipWidth, pipHeight, containerWidth, containerHeight) => {
    const clampedX = Math.min(Math.max(0, x), containerWidth - pipWidth);
    const clampedY = Math.min(Math.max(0, y), containerHeight - pipHeight);
    return { x: clampedX, y: clampedY };
};

const fullScreenSelector = (state) => state.fullscreen;
const showPictureInPictureSelector = (state) => state.showPictureInPicture;
const mainSourceIndexSelector = (state) => state.playlist.position.sourceIndex;

const offsetSelector = (state) => state.playlist.data.offsets;
const geometrySelector = (state) => state.playlist.data.route_locations_v2;
const currentDistanceSelector = (state) => state.playlist.position.playlistDistance;

const zoomableImageDimensionsSelector = (state) => state.zoomableImageDimensions;

const getDefaultPipSourceIndex = (sources, mainSourceIndex) => {
    if (!sources || sources.length === 0) return 0;
    for (const source of sources) {
        const { index } = source;

        if (index === null || index === undefined) {
            console.error("Source missing index");
            continue;
        }

        if (index !== mainSourceIndex) return index;
    }
    return 0;
};

const PictureInPicture = memo(({ sources, parentIsSlave, videoContainerRef, selectStream }) => {
    const dragStartRef = useRef(null);

    const lastSizeRef = useRef({ width: 0, height: 0 });

    const [movedByUser, setMovedByUser] = useState(false);
    const [{ width, height }, setDimensions] = useState(DEFAULT_WIDTH_HEIGHT);
    const [draggablePosition, setDraggablePosition] = useState({ x: 0, y: 0 });

    const fullscreen = useSelector(fullScreenSelector);
    const mainSourceIndex = useSelector(mainSourceIndexSelector);
    const showPictureInPicture = useSelector(showPictureInPictureSelector);

    const offsets = useSelector(offsetSelector);
    const geometry = useSelector(geometrySelector);
    const distance = useSelector(currentDistanceSelector);
    const zoomableImageDimensions = useSelector(zoomableImageDimensionsSelector);

    const [sourceIndex, setSourceIndex] = useState(getDefaultPipSourceIndex(sources, mainSourceIndex));

    const dispatch = useDispatch();

    const saveDraggablePosition = useCallback((data) => {
        setDraggablePosition({ x: data.x, y: data.y });
    }, []);

    const timestamp = useMemo(() => {
        const pipOffset = offsets ? (offsets[sourceIndex] ?? 0) : 0;

        const pipDistance = distance + pipOffset;

        return playlistDistanceToTimestamp(pipDistance, geometry);
    }, [distance, geometry, offsets, sourceIndex]);

    const handleSwitch = useCallback(() => {
        setSourceIndex(mainSourceIndex);
        selectStream({ target: { value: `${sourceIndex}.2` } });
    }, [mainSourceIndex, selectStream, sourceIndex]);

    const handleDrag = useCallback(
        (data) => {
            if (movedByUser) {
                saveDraggablePosition(data);
                return;
            }

            if (!dragStartRef.current) {
                dragStartRef.current = { x: data.x, y: data.y };
            }

            const dx = Math.abs(data.x - dragStartRef.current.x);
            const dy = Math.abs(data.y - dragStartRef.current.y);

            if (dx > 2 || dy > 2) {
                setMovedByUser(true);
            }

            saveDraggablePosition(data);
        },
        [movedByUser, saveDraggablePosition],
    );

    const handleStop = useCallback(
        (data) => {
            dragStartRef.current = null;
            saveDraggablePosition(data);
        },
        [saveDraggablePosition],
    );

    const Icons = useMemo(() => {
        if (!sources) return null;

        const streams = [];

        for (const source of sources) {
            const { index } = source;

            if (index === null || index === undefined) {
                console.error("Source is missing index");
                continue;
            }

            if (index === mainSourceIndex) continue;

            const stillsStream = source.streams.find((stream) => stream.isStills);
            if (!stillsStream) continue;

            stillsStream["parentSource"] = source.name;
            streams.push({ stream: stillsStream, index });
        }

        return (
            <div className="pip-button-group">
                {streams.map(({ stream, index }) => (
                    <Tooltip
                        title={stream.parentSource}
                        key={index}>
                        <div
                            className={`pip-button ${index === sourceIndex ? "selected" : ""} ${streams.length === 1 && "disabled"}`}
                            onClick={() => setSourceIndex(index)}>
                            {getIcon(stream.parentSource)}
                        </div>
                    </Tooltip>
                ))}
            </div>
        );
    }, [sources, mainSourceIndex, sourceIndex]);

    const calculateDimensions = useCallback(
        (videoContainerWidth) => {
            const width = videoContainerWidth * 0.33;

            const aspect = zoomableImageDimensions.width ? zoomableImageDimensions.height / zoomableImageDimensions.width : NINE_SIXTEEN;
            const height = width * aspect;

            return { width, height };
        },
        [zoomableImageDimensions.height, zoomableImageDimensions.width],
    );

    const handleParentResize = useCallback(
        (entries) => {
            const videoContainerEntry = entries[0];
            if (!videoContainerEntry) return;

            const { width: containerWidth, height: containerHeight } = videoContainerEntry.contentRect;
            const lastSize = lastSizeRef.current;

            if (containerWidth === lastSize.width && containerHeight === lastSize.height) {
                return;
            }

            lastSizeRef.current = { width: containerWidth, height: containerHeight };
            const { width: pipWidth, height: pipHeight } = calculateDimensions(containerWidth);
            setDimensions({ width: pipWidth, height: pipHeight });
            saveDraggablePosition(calculateXY(pipWidth, pipHeight, containerWidth, containerHeight));
        },
        [calculateDimensions, saveDraggablePosition],
    );

    useEffect(() => {
        if (!sources) return;
        if (sourceIndex === mainSourceIndex) {
            setSourceIndex(getDefaultPipSourceIndex(sources, mainSourceIndex));
        }
    }, [mainSourceIndex, sourceIndex, sources]);

    useEffect(() => {
        if (!videoContainerRef || !sources) return;

        const videoContainer = videoContainerRef.current;
        if (!videoContainer) return;

        const { clientWidth: containerWidth, clientHeight: containerHeight } = videoContainer;
        const { width, height } = calculateDimensions(containerWidth);

        setDimensions({ width, height });
        setDraggablePosition((pos) => {
            if (movedByUser) {
                return clampToBounds(pos.x, pos.y, width, height, containerWidth, containerHeight);
            }
            return calculateXY(width, height, containerWidth, containerHeight);
        });
    }, [calculateDimensions, saveDraggablePosition, sources, videoContainerRef, showPictureInPicture, movedByUser]);

    useEffect(() => {
        if (!videoContainerRef || parentIsSlave) return;

        const resizeObserver = new ResizeObserver(handleParentResize);
        const videoContainer = videoContainerRef.current;

        resizeObserver.observe(videoContainer);

        return () => {
            resizeObserver.unobserve(videoContainer);
            resizeObserver.disconnect();
        };
    }, [parentIsSlave, videoContainerRef, handleParentResize]);

    useEffect(() => {
        setMovedByUser(false);
    }, [fullscreen]);

    useEffect(() => {
        if (!sources) return;

        const isInvalid = sourceIndex < 0 || sourceIndex >= sources.length;

        if (!isInvalid) return;

        setSourceIndex(getDefaultPipSourceIndex(sources, mainSourceIndex));
    }, [mainSourceIndex, sourceIndex, sources]);

    if (parentIsSlave || !showPictureInPicture || !videoContainerRef) return null;

    return (
        <Draggable
            draggable
            bounds="parent"
            disabled={false}
            position={draggablePosition}
            onDrag={(_, data) => handleDrag(data)}
            onStop={(_, data) => handleStop(data)}>
            <div
                className="pip-container absolute"
                style={{ width, height }}>
                <div
                    className="PictureInPictureBanner"
                    style={{
                        paddingLeft: Math.max(8, VERTICAL_NAV_WIDTH - draggablePosition.x + 8),
                        paddingRight: Math.max(8, draggablePosition.x + width - (videoContainerRef?.current?.clientWidth ?? 8) + VERTICAL_NAV_WIDTH + 8),
                    }}>
                    {Icons}

                    <div className="w-full flex justify-end gap-4">
                        <Tooltip title="Switch">
                            <div
                                className="pip-button"
                                onClick={handleSwitch}>
                                <FontAwesomeIcon icon={faRetweetAlt} />
                            </div>
                        </Tooltip>
                        <div
                            className="pip-button"
                            onClick={() => dispatch(togglePictureInPicture(false))}>
                            <FontAwesomeIcon icon={faTimes} />
                        </div>
                    </div>
                </div>

                <ImagePlayer
                    isStills
                    isSlave
                    playSpeed={1}
                    zoomable={false}
                    isPictureInPicture
                    sourceIndex={sourceIndex}
                    playerControls={false}
                    newAbsoluteTimestamp={timestamp}
                />
            </div>
        </Draggable>
    );
});

export default PictureInPicture;
